import { Toggle, Button } from '@eye-able/ui';
import { Undo2 } from 'lucide-react';
import { FlagIcon } from '@/i18n/dialog/FlagIcon';
import { memo } from 'react';
import { cn } from '@helpers/cn.ts';

interface LanguageRowProps {
  language: string;
  isActive: boolean;
  canEdit: boolean;
  canTranslate: boolean;
  translation: string;
  translationOriginal: string;
  selectedOption: { key: string };
  onTextChange: (language: string, value: string) => void;
  onRevertClick: (language: string) => void;
  onLanguageToggle: (isActive: boolean, language: string) => void;
  translate: (text: string, language: string) => Promise<void>;
}

export const LanguageRow = memo((props: LanguageRowProps) => {
  const {
    canEdit,
    isActive,
    language,
    translation,
    translationOriginal,
    selectedOption,
    onTextChange,
    onRevertClick,
    onLanguageToggle,
    translate,
    canTranslate,
  } = props;

  return (
    <div className='eye:flex eye:flex-col-2 eye:items-start eye:gap-4xs'>
      <div className='eye:flex eye:flex-col'>
        {canTranslate ? (
          <Button
            className='eye:p-12'
            disabled={!isActive}
            title={`perform automatic translation based on reference language: ${language}`}
            size='sm'
            variant='ghost'
            onClick={() => {
              void translate(translation, language);
            }}
          >
            <FlagIcon language={language} />
          </Button>
        ) : (
          <FlagIcon language={language} />
        )}

        <Toggle
          disabled={!canEdit}
          checked={isActive && canEdit}
          title={'exclude language from automatic translation / saving'}
          onCheckedChange={(checked) => onLanguageToggle(checked, language)}
        />
      </div>

      <textarea
        className={cn(
          'eye:min-h-48px eye:focus:min-h-[150px] eye:transition-all eye:duration-300',
          'eye:resize-none eye:border eye:border-grey-40 eye:rounded-md eye:p-4 eye:w-full',
          'eye:disabled:bg-grey-20 eye:disabled:text-grey-120',
          isActive ? '' : 'eye:opacity-50',
          translation === selectedOption.key
            ? 'eye:bg-red-25'
            : translation === translationOriginal
              ? 'eye:bg-white'
              : 'eye:bg-green-25',
        )}
        disabled={!isActive || !canEdit}
        value={translation}
        onChange={(e) => onTextChange(language, e.target.value)}
      />
      {canEdit && (
        <Button
          disabled={translation === translationOriginal}
          size='sm'
          title='revert changes'
          variant='ghost'
          onClick={() => {
            onRevertClick(language);
          }}
        >
          <Undo2 className='eye:size-16' />
        </Button>
      )}
    </div>
  );
});

LanguageRow.displayName = 'LanguageRow';
